import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {CSSProperties, ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import DocumentSettings from "../../../document/DocumentSettings";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	PaddedRectangle,
	ScalePagesToolboxScale,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import ScaleDialogFields from "../../field/page/ScaleDialogFields";
import PaperHelper, {PaperFormat, paperSizes} from "../../../../shared/helper/PaperHelper";
import {PortalToolboxScaleScale} from "../../../../shared/models/PortalToolboxScaleScale";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {PREVIEW_SIZE} from "../../../startscreen/Preview";
import PageRange from "../../../../shared/components/PageRange";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import {InputSwitch} from "primereact/inputswitch";
import FormatEnum from "../../../../shared/models/FormatEnum";
import OrientationEnum from "../../../../shared/models/OrientationEnum";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import UserSettings from "../../../session/UserSettings";
import Immutable from "../../../../shared/immutable/Immutable";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface ScaleDialogProperties extends PortalDialogProperties<PortalToolboxScaleScale> {
	userSettings: Immutable<UserSettings>;
}

interface ScaleDialogStatus extends PortalDialogState<PortalToolboxScaleScale> {
	pages: number;
	currentPage: string;
	unit: LengthUnit;
}

export class ScaleDialogDefinition extends PortalDialog<PortalToolboxScaleScale, ScaleDialogProperties,
	ScaleDialogStatus> {
	constructor(props: ScaleDialogProperties) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<ScaleDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.scalePages, Functions.getScalePagesDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.SCALE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["scale-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.scale.general"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderPreview(parameter: PortalToolboxScaleScale): ReactNode {
		let width: number = FunctionHelper.applyValue(
			parameter.scalePages?.dimensions?.width, PaddedRectangle.getWidthDefault()
		);
		let height: number = FunctionHelper.applyValue(
			parameter.scalePages?.dimensions?.height, PaddedRectangle.getHeightDefault()
		);
		let scale: number = Math.max(width, height) / PREVIEW_SIZE;

		let pageStyle: CSSProperties = {
			width: (width / scale) + LengthUnit.PIXEL,
			height: (height / scale) + LengthUnit.PIXEL
		};

		return (
			<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left">
				<fieldset className="p-col-12 p-grid" hidden={!this.state.fields?.contains(ScaleDialogFields.PREVIEW)}>
					<div className="p-col-12 p-grid">
						<div className="page-preview">
							<div style={pageStyle}/>
						</div>
					</div>
				</fieldset>
			</div>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxScaleScale | undefined = this.state.parameter?.data;
		let errors: Map<ScaleDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let pageFormats: Array<any> = [
			{label: Translate.value("scale.dimensions.custom", {ns: "dialog"}), value: FormatEnum.CUSTOM}
		];

		for (let formatGroup of paperSizes) {
			pageFormats.push({
				label: Translate.value("papersize." + formatGroup.name),
				className: "group",
				value: formatGroup.name,
				disabled: true
			});

			for (let paperFormat of formatGroup.values) {
				pageFormats.push({label: paperFormat.name, className: "item", value: paperFormat.name});
			}
		}

		return (
			<>
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(ScaleDialogFields.FORMAT)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-format">
								<Translate {...this.props} data="scale.dimensions.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7 p-grid">
								<Dropdown inputId="control-format" options={pageFormats}
										  value={FunctionHelper.applyValue(
											  parameter?.format, PortalToolboxScaleScale.getFormatDefault()
										  )}
										  onChange={(e) => this.onChange(
											  ScaleDialogFields.FORMAT, e.value
										  )} appendTo={document.getElementById("root")}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							parameter?.format !== FormatEnum.CUSTOM ||
							!this.state.fields?.contains(ScaleDialogFields.WIDTH)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-width">
								<Translate {...this.props} data="scale.width" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7"
								 hidden={!this.state.fields?.contains(ScaleDialogFields.WIDTH)}>
								<InputNumber inputId="control-width" step={1} suffix={" " + this.state.unit}
											 maxFractionDigits={2} minFractionDigits={2} min={0} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter?.scalePages?.dimensions?.width,
													 PaddedRectangle.getWidthDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ScaleDialogFields.WIDTH, e.value
											 )}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							parameter?.format !== FormatEnum.CUSTOM ||
							!this.state.fields?.contains(ScaleDialogFields.HEIGHT)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-height">
								<Translate {...this.props} data="scale.height" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7"
								 hidden={!this.state.fields?.contains(ScaleDialogFields.HEIGHT)}>
								<InputNumber inputId="control-height" step={1} suffix={" " + this.state.unit}
											 maxFractionDigits={2} minFractionDigits={2} min={0} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter?.scalePages?.dimensions?.height,
													 PaddedRectangle.getHeightDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ScaleDialogFields.HEIGHT, e.value
											 )}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={
								 parameter?.format === FormatEnum.CUSTOM ||
								 !this.state.fields?.contains(ScaleDialogFields.ORIENTATION)
							 }>
							<label className="p-col-12 p-sm-5" htmlFor="control-orientation">
								<Translate {...this.props} data="scale.orientation.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7 p-grid">
								<Dropdown inputId="control-orientation"
										  value={FunctionHelper.applyValue(
											  parameter?.orientation,
											  PortalToolboxScaleScale.getOrientationDefault()
										  )}
										  options={[
											  {
												  label: Translate.value(
													  "scale.orientation.vertical", {ns: "dialog"}
												  ),
												  value: OrientationEnum.VERTICAL
											  },
											  {
												  label: Translate.value(
													  "scale.orientation.horizontal", {ns: "dialog"}
												  ),
												  value: OrientationEnum.HORIZONTAL
											  }
										  ]}
										  onChange={(e) => this.onChange(
											  ScaleDialogFields.ORIENTATION, e.value
										  )}
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(ScaleDialogFields.MARGIN_TOP)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-margin-top">
								<Translate {...this.props} data="scale.margin.top" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<div className="p-inputgroup">
									<InputNumber inputId="control-margin-top" step={1} suffix={" " + this.state.unit}
												 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
												 useGrouping={false}
												 value={UnitHelper.unitToUnit(
													 FunctionHelper.applyValue(
														 parameter?.scalePages?.dimensions?.top,
														 PaddedRectangle.getTopDefault()
													 ), LengthUnit.MILLIMETER, this.state.unit,
													 this.props.portalResolution
												 )}
												 onChange={(e) => this.onChange(
													 ScaleDialogFields.MARGIN_TOP, e.value
												 )}/>
									<div className="p-inputgroup-addon vertical">
										<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
												onClick={() => this.onChange(
													ScaleDialogFields.MARGIN_TOP, PaddedRectangle.getTopDefault()
												)}/>
									</div>
								</div>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(ScaleDialogFields.MARGIN_BOTTOM)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-margin-bottom">
								<Translate {...this.props} data="scale.margin.bottom" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<div className="p-inputgroup">
									<InputNumber inputId="control-margin-bottom" step={1} suffix={" " + this.state.unit}
												 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
												 useGrouping={false}
												 value={UnitHelper.unitToUnit(
													 FunctionHelper.applyValue(
														 parameter?.scalePages?.dimensions?.bottom,
														 PaddedRectangle.getBottomDefault()
													 ), LengthUnit.MILLIMETER, this.state.unit,
													 this.props.portalResolution
												 )}
												 onChange={(e) => this.onChange(
													 ScaleDialogFields.MARGIN_BOTTOM, e.value
												 )}/>
									<div className="p-inputgroup-addon vertical">
										<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
												onClick={() => this.onChange(
													ScaleDialogFields.MARGIN_BOTTOM, PaddedRectangle.getBottomDefault()
												)}/>
									</div>
								</div>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(ScaleDialogFields.MARGIN_LEFT)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-margin-left">
								<Translate {...this.props} data="scale.margin.left" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<div className="p-inputgroup">
									<InputNumber inputId="control-margin-left" step={1} suffix={" " + this.state.unit}
												 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
												 useGrouping={false}
												 value={UnitHelper.unitToUnit(
													 FunctionHelper.applyValue(
														 parameter?.scalePages?.dimensions?.left,
														 PaddedRectangle.getLeftDefault()
													 ), LengthUnit.MILLIMETER, this.state.unit,
													 this.props.portalResolution
												 )}
												 onChange={(e) => this.onChange(
													 ScaleDialogFields.MARGIN_LEFT, e.value
												 )}/>
									<div className="p-inputgroup-addon vertical">
										<Button icon="icon icon-close"
												tooltip={Translate.value("inputs.clear")}
												onClick={() => this.onChange(
													ScaleDialogFields.MARGIN_LEFT, PaddedRectangle.getLeftDefault()
												)}/>
									</div>
								</div>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(ScaleDialogFields.MARGIN_RIGHT)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-margin-right">
								<Translate {...this.props} data="scale.margin.right" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<div className="p-inputgroup">
									<InputNumber inputId="control-margin-right" step={1} suffix={" " + this.state.unit}
												 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
												 useGrouping={false}
												 value={UnitHelper.unitToUnit(
													 FunctionHelper.applyValue(
														 parameter?.scalePages?.dimensions?.right,
														 PaddedRectangle.getRightDefault()
													 ), LengthUnit.MILLIMETER, this.state.unit,
													 this.props.portalResolution
												 )}
												 onChange={(e) => this.onChange(
													 ScaleDialogFields.MARGIN_RIGHT, e.value
												 )}/>
									<div className="p-inputgroup-addon vertical">
										<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
												onClick={() => this.onChange(
													ScaleDialogFields.MARGIN_RIGHT, PaddedRectangle.getRightDefault()
												)}/>
									</div>
								</div>
							</div>
						</div>
					</fieldset>
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(ScaleDialogFields.ASPECT_RATIO)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-aspect-ratio"/>
							<div className="p-col-12 p-sm-7 switch-container">
								<InputSwitch inputId="control-aspect-ratio"
											 checked={FunctionHelper.applyValue(
												 parameter?.scalePages?.preserveAspectRatio,
												 ScalePagesToolboxScale.getPreserveAspectRatioDefault()
											 )}
											 onChange={(e) => this.onChange(
												 ScaleDialogFields.ASPECT_RATIO, e.value
											 )}/>
								<label className="switch-label" htmlFor="control-aspect-ratio">
									<Translate {...this.props} data="scale.aspect_ratio.label" namespace="dialog"/>
								</label>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(ScaleDialogFields.BOXES)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-boxes"/>
							<div className="p-col-12 p-sm-7 switch-container">
								<InputSwitch inputId="control-boxes"
											 checked={FunctionHelper.applyValue(
												 parameter?.scalePages?.scaleBoxes,
												 ScalePagesToolboxScale.getScaleBoxesDefault()
											 )}
											 onChange={(e) => this.onChange(
												 ScaleDialogFields.BOXES, e.value
											 )}/>
								<label className="switch-label" htmlFor="control-boxes">
									<Translate {...this.props} data="scale.boxes.label" namespace="dialog"/>
								</label>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(ScaleDialogFields.PAGERANGE)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-pagerange">
								<Translate {...this.props} data="scale.pages.label" namespace="dialog"/>
								<Info {...this.props} data="scale.pages.info" options={{ns: "dialog"}}/>
							</label>
							<div className="p-col-12 p-sm-7">
								<PageRange inputId="control-pagerange"
										   className={errors.has(ScaleDialogFields.PAGERANGE) ? " p-invalid" : ""}
										   value={FunctionHelper.applyValue(
											   parameter?.scalePages?.pages, ScalePagesToolboxScale.getPagesDefault()
										   )}
										   onChange={(e) => this.onChange(
											   ScaleDialogFields.PAGERANGE, e.value
										   )}
										   appendTo={document.getElementById("root")} editable={true}
										   addon={Translate.value(
											   "inputs.pagerange.of", {pages: this.state.pages}
										   )}
										   tooltip={errors.has(ScaleDialogFields.PAGERANGE) ? Translate.value(
											   errors.get(ScaleDialogFields.PAGERANGE)!, {ns: "message"}
										   ) : ""}
										   options={[
											   {
												   label: Translate.value("inputs.pagerange.all"),
												   value: PAGERANGE_ALL
											   },
											   {
												   label: Translate.value("inputs.pagerange.current"),
												   value: this.state.currentPage
											   }
										   ]}/>
								{this.renderFieldError(errors.has(ScaleDialogFields.PAGERANGE) ?
									Translate.value(
										errors.get(ScaleDialogFields.PAGERANGE)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
					</fieldset>
				</div>

				{this.renderPreview(parameter!)}
			</>
		);
	}

	protected onChange(field: ScaleDialogFields, value: any): void {
		let parameter: PortalToolboxScaleScale = this.state.parameter!.data;
		let errors: Map<ScaleDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case ScaleDialogFields.FORMAT:
				parameter.format = value;

				let format: PaperFormat | null = PaperHelper.findFormat(value);
				if (!FunctionHelper.exists(format)) {
					break;
				}

				let width: number = parameter.orientation === OrientationEnum.VERTICAL ? format!.width : format!.height;
				let height: number = parameter.orientation === OrientationEnum.VERTICAL ? format!.height : format!.width;
				parameter.scalePages!.dimensions!.width = width;
				parameter.scalePages!.dimensions!.height = height;
				break;
			case ScaleDialogFields.WIDTH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PaddedRectangle.getWidthDefault();
				}

				parameter.scalePages!.dimensions!.width = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ScaleDialogFields.HEIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PaddedRectangle.getHeightDefault();
				}

				parameter.scalePages!.dimensions!.height = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ScaleDialogFields.ORIENTATION:
				if (parameter.orientation === value) {
					break;
				}

				parameter.orientation = value;
				let tempSize: number = FunctionHelper.applyValue(
					parameter.scalePages?.dimensions?.width, PaddedRectangle.getWidthDefault()
				);
				parameter.scalePages!.dimensions!.width = FunctionHelper.applyValue(
					parameter.scalePages?.dimensions?.height, PaddedRectangle.getHeightDefault()
				);
				parameter.scalePages!.dimensions!.height = tempSize;
				break;
			case ScaleDialogFields.MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PaddedRectangle.getTopDefault();
				}

				parameter.scalePages!.dimensions!.top = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ScaleDialogFields.MARGIN_BOTTOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PaddedRectangle.getBottomDefault();
				}

				parameter.scalePages!.dimensions!.bottom = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ScaleDialogFields.MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PaddedRectangle.getLeftDefault();
				}

				parameter.scalePages!.dimensions!.left = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ScaleDialogFields.MARGIN_RIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PaddedRectangle.getRightDefault();
				}

				parameter.scalePages!.dimensions!.right = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ScaleDialogFields.ASPECT_RATIO:
				parameter.scalePages!.preserveAspectRatio = value;
				break;
			case ScaleDialogFields.BOXES:
				parameter.scalePages!.scaleBoxes = value;
				break;
			case ScaleDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.scalePages!.pages = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-scale small"}/>
					<Translate {...this.props} data="scale.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxScaleScale | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		parameter!.scalePages!.pages = documentSettings.pageSelection.toString(
			pageCount, documentSettings.activeViewer.currentPage
		);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage,
			unit: userSettings.unit
		});
	};

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-grid">
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<div className="p-col-12 p-grid">
								<Button label={Translate.value("buttons.reset")} icon={"icon icon-rotate-270 small"}
										disabled={this.state.submitted} className="reset-button"
										onClick={() => this.resetParameter()}/>
								<Button label={Translate.value("buttons.submit")} disabled={!this.isValid()}
										icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
										className="primary-button" onClick={() => this.submit()}/>
								{this.state.submitted ?
									<Button label={Translate.value("buttons.close")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/> :
									<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/>
								}
							</div>
						</div>
					</fieldset>
				</div>
				<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left-hidden"></div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		let errors: Map<any, string> = this.state.fieldErrors;
		return errors.size === 0 && !this.state.submitted;
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			scale: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.scale", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.SCALE;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxScaleScale> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const ScaleDialog = React.memo(ScaleDialogDefinition, (
		prevProps: Readonly<ScaleDialogProperties>, nextProps: Readonly<ScaleDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default ScaleDialog;